import React, { useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useParams, withRouter } from 'react-router-dom';
import { Alert, Button, Content, Notification, Panel, Toggle } from 'rsuite';
import { trackEvent } from '../../../../../utils/MatomoConfig';

import { LoadingSpiner, LoadingSpinerArea } from '../../../../../components/LoadingSpinner';
import ModalConfirm from '../../../../../components/ModalConfirm';
import ConsolidateItems from '../Components/ConsolidateItems';
import DateIntervals from '../Components/DateIntervals';
import DndComponent from '../Components/DnD/DndComponent';
import ModalSaveReport from '../Components/ModalSaveReport';
import ModalUnderstandBetter from '../Components/ModalUnderstandBetter';

import {
  setGenerateReportData,
  setGenerateReportResponse,
  setModuloRelatorioDataToInitialData,
  setStateValue,
} from '../../../../../actions/actionsModuloRelatorio';
import { clearFiltros } from '../../../../../actions/actionsPainelGeral';
import {
  checkWithin90Days,
  dimensionsSelectionMissingText,
  dndLocalItems,
  dndPriceItems,
  dndProductItems,
  dndQuantityItems,
  dndWithFiltersItems,
  reportNameAndInitialDateMissingText,
} from '../utils';

import iconCheckGreen from '../../../../../assets/icons/icon_check_circle_green.svg';
import saveIcon from '../../../../../assets/icons/icon_save_blue.svg';
import { useDndComponentToEditItems, useGenerateReport, useSavedFiltersForEdition, useSaveReportCounter, useSelectCnpj, useSelectEndereco } from '../Hooks';
import { useProductModuloRelatorio } from '../Hooks/useProductModuloRelatorio';
import { fileDownloadStatus, PRICE_TYPE_TOGGLE_DATA } from '../data';

const ColumnSectionTitle = ({ title }) => (
  <div className="text-border-wrapper">
    <p className="text">{title}</p>
    <div className="side-border" />
  </div>
);

const PanelHeader = ({ title, subtitle }) => (
  <div className="panel-header-title-wrapper">
    <p>{title}</p>
    <p className="panel-header-subtitle">{subtitle}</p>
  </div>
);

const DndComponentMemo = React.memo(DndComponent);

const CreateReportContent = ({ goToContent, onClickGoBack }) => {
  const [showModalUnderstandBetter, setShowModalUnderstandBetter] = useState(false);
  const [priceType, setPriceType] = useState({
    precoPago: true,
    precoRegular: false,
    precoUnidadeMedida: false,
  });
  const [showModalSaveReport, setShowModalSaveReport] = useState(false);

  const productsEditList = useDndComponentToEditItems(dndProductItems);
  const localEditList = useDndComponentToEditItems(dndLocalItems);
  const priceEditList = useDndComponentToEditItems(dndPriceItems);
  const quantityEditList = useDndComponentToEditItems(dndQuantityItems);
  const withFiltersEditList = useDndComponentToEditItems(dndWithFiltersItems);

  const { setDimensoesList, validateConsolidadoData, consolidateSelectedList, setConsolidateSelectedList } = useGenerateReport();

  const { products, onSearchProducts, handleAddProduct, handleRemoveProduct, handleCleanProduct, setProductsForEdition } = useProductModuloRelatorio();

  const { enderecoHandlers } = useSelectEndereco();
  const { cnpjHandlers } = useSelectCnpj();
  const { refetchCounter, dataCounter, isFetching } = useSaveReportCounter();
  const { handleSetDimensoesFilters } = useSavedFiltersForEdition({ setProductsForEdition, enderecoHandlers });

  const dispatch = useDispatch();

  const {
    consolidadoData,
    moduloRelatorioData,
    reportSaveSuccess,
    saveReportItemData,
    showModalConfirmDownload,
    dateIntervalKey,
    filterProductEditionLoading,
  } = useSelector(
    state => ({
      consolidadoData: state.moduloRelatorioDataReducer?.consolidadoData,
      moduloRelatorioData: state.moduloRelatorioDataReducer?.moduloRelatorioData,
      showModalConfirmDownload: state.moduloRelatorioDataReducer?.showModalConfirmDownload,
      reportSaveSuccess: state.moduloRelatorioDataReducer?.reportSaveSuccess,
      saveReportItemData: state.moduloRelatorioDataReducer?.saveReportItemData,
      dateIntervalKey: state.moduloRelatorioDataReducer?.dateIntervalKey,
      filterProductEditionLoading: state.moduloRelatorioDataReducer?.filterProductEditionLoading,
    }),
    shallowEqual,
  );

  const functions = {
    onSearchProducts,
    handleAddProduct,
    handleRemoveProduct,
    handleCleanProduct,
  };

  const { id } = useParams();

  const buildPriceTypeArray = (precoPago, precoRegular, precoUnidadeMedida) => {
    const priceTypes = [];

    if (precoPago) priceTypes.push('PRECO_PAGO');
    if (precoRegular) priceTypes.push('PRECO_REGULAR');
    if (precoUnidadeMedida) priceTypes.push('PRECO_POR_UNIDADE_MEDIDA');

    return priceTypes;
  };

  const handlePriceType = (checked, name) => {
    setPriceType(prev => {
      if (!name) return prev;

      const isUncheck = !checked;
      if (name === 'precoPago' && isUncheck && prev.precoPago && !prev.precoRegular) return prev;
      if (name === 'precoRegular' && isUncheck && prev.precoRegular && !prev.precoPago) return prev;

      const next = { ...prev, [name]: checked };
      const priceTypes = buildPriceTypeArray(next.precoPago, next.precoRegular, next.precoUnidadeMedida);
      dispatch(setGenerateReportData('precoEscolhido', priceTypes));
      return next;
    });
  };

  const handleGenerateReport = () => {
    setDimensoesList();
    validateConsolidadoData('generate');
  };

  const validateAddressAndGenerateReport = type => {
    const addressInColumn2 = consolidadoData.local.some(({ name, column }) => name === 'Endereço' && column === '2');

    const { latitude, raio } = moduloRelatorioData.enderecoDTO;

    const isAddressFilled = typeof latitude === 'number' && typeof raio === 'number';
    const isAddressEmpty = latitude === null && raio === null;

    const canGenerateReport = !addressInColumn2 || isAddressFilled || isAddressEmpty;

    if (canGenerateReport) {
      setShowModalSaveReport(true);
      return;
    }

    Alert.warning('Endereço ou raio não foram selecionados', 5000);
  };

  const handleSaveReport = () => {
    if (
      consolidadoData.produto.length &&
      consolidadoData.local.length &&
      (consolidadoData.preco.length || consolidadoData.quantidade.length || consolidadoData.comFiltro.length)
    ) {
      refetchCounter();
      validateAddressAndGenerateReport('save');
    } else {
      Alert.warning(dimensionsSelectionMissingText, 5000);
    }
  };

  const handleOpenModalGenerate = () => {
    if (
      consolidadoData.produto.length &&
      consolidadoData.local.length &&
      (consolidadoData.preco.length || consolidadoData.quantidade.length || consolidadoData.comFiltro.length)
    ) {
      // abre a modal de Processar Relatório quando a chamada do endpoint abaixo for 200
      handleGenerateReport();
      trackEvent('Módulo de relatórios', 'Gerar relatório');
    } else {
      Alert.warning(dimensionsSelectionMissingText, 5000);
    }
  };

  const handleSelectGenerateReportModal = () => {
    dispatch(setGenerateReportResponse(false));
    goToContent('HISTORICO_RELATORIOS');
  };

  const setSelectSavedReportOptions = savedData => {
    // fixa os valores nos filtros e coluna2 para EDIÇÃO/SALVO
    setPriceType({
      precoPago: savedData?.precoEscolhido?.some(item => item === 'PRECO_PAGO'),
      precoRegular: savedData?.precoEscolhido?.some(item => item === 'PRECO_REGULAR'),
      precoUnidadeMedida: savedData?.precoEscolhido?.some(item => item === 'PRECO_POR_UNIDADE_MEDIDA'),
    });
    handleSetDimensoesFilters(savedData);
  };

  const handleSaveEditedReport = () => {
    const isOver90Days = checkWithin90Days(moduloRelatorioData.dataInicio, moduloRelatorioData.dataFim);

    if (isOver90Days) {
      dispatch(setStateValue('openModalGenerateReportError', true));
      return;
    }

    if (moduloRelatorioData?.recorrencia) {
      if (
        moduloRelatorioData?.fileName === '' ||
        moduloRelatorioData?.reportEngineRecorrenciaDTO?.dataInicioEnvio === '' ||
        moduloRelatorioData?.reportEngineRecorrenciaDTO?.dataInicioEnvio === null
      ) {
        Alert.warning(reportNameAndInitialDateMissingText, 5000);
        return;
      }
      validateConsolidadoData('update');
    } else {
      if (moduloRelatorioData?.fileName === '') {
        Alert.warning(reportNameAndInitialDateMissingText, 5000);
        return;
      }
      validateConsolidadoData('update');
    }
  };

  useEffect(() => {
    setDimensoesList();
  }, [consolidadoData]);

  useEffect(() => {
    if (showModalConfirmDownload) {
      setPriceType({
        precoPago: true,
        precoRegular: false,
      });
    }

    if (reportSaveSuccess) {
      Notification.open({
        className: 'product-config-success-notification',
        description: (
          <>
            <img src={iconCheckGreen} alt="" />
            <p>Relatório salvo com sucesso.</p>
          </>
        ),
      });
      setShowModalSaveReport(false);
      dispatch(setStateValue('reportSaveSuccess', false));
      dispatch(setStateValue('saveReportItemData', {}));
      onClickGoBack();
    }
  }, [showModalConfirmDownload, reportSaveSuccess]);

  useEffect(() => {
    if (moduloRelatorioData?.produto?.length !== products?.cache?.length) {
      dispatch(setGenerateReportData('produto', products.value));
    }
  }, [dispatch, moduloRelatorioData?.produto?.length, products?.cache, products.value]);

  useEffect(() => {
    if (typeof id === 'string' && id === saveReportItemData?.id) {
      setSelectSavedReportOptions(saveReportItemData?.reportEngineDTO);
    }

    if (typeof id !== 'string') {
      onSearchProducts();
      dispatch(clearFiltros());
      dispatch(setModuloRelatorioDataToInitialData());
    }
  }, [id, saveReportItemData?.id, saveReportItemData?.reportEngineDTO?.produto?.length]);

  return (
    <>
      <Content>
        {filterProductEditionLoading ? <LoadingSpinerArea size="md" isLoading={filterProductEditionLoading} /> : <LoadingSpiner size="md" />}
        {typeof id === 'string' ? null : <p className="content-title">Criar relatório</p>}

        <p className="content-subtitle">
          <span className="bold-text">DEFINIÇÃO DE COLUNAS</span>
          <span className="regular-text">Defina pelo menos um elemento (coluna) para cada dimensão.</span>{' '}
          <span className="link-text">
            <button type="button" onClick={() => setShowModalUnderstandBetter(true)}>
              Entenda melhor
            </button>
          </span>
        </p>
        <Panel
          header={<PanelHeader title="Intervalo de datas" subtitle="Escolha um período e uma quebra de período para o relatório" />}
          collapsible
          bordered
          className="date-interval-panel"
          defaultExpanded
        >
          <DateIntervals key={dateIntervalKey} />
        </Panel>

        <Panel
          header={<PanelHeader title="Produto" subtitle="Escolha quais colunas de produto devem aparecer no relatório arrastando o elemento desejado abaixo" />}
          collapsible
          bordered
          className="product-panel"
          defaultExpanded
        >
          <DndComponentMemo
            key="produto"
            dndName="produto"
            dndItems={typeof id === 'string' ? productsEditList.editItems : dndProductItems}
            products={products}
            functions={functions}
            columnTitles={['Elementos disponíveis', 'Elementos selecionados (colunas)']}
          />
        </Panel>
        <Panel
          header={<PanelHeader title="Local" subtitle="Escolha quais colunas de local devem aparecer no relatório arrastando o elemento desejado abaixo" />}
          collapsible
          bordered
          className="product-panel"
          defaultExpanded
        >
          <DndComponentMemo
            key="local"
            dndName="local"
            dndItems={typeof id === 'string' ? localEditList.editItems : dndLocalItems}
            columnTitles={['Elementos disponíveis', 'Elementos selecionados (colunas)']}
            enderecoHandlers={enderecoHandlers}
            cnpjHandlers={cnpjHandlers}
          />
        </Panel>
        <Panel
          header={
            <PanelHeader
              title="Métricas"
              dataLength={consolidadoData.preco.length || consolidadoData.quantidade.length || consolidadoData.comFiltro.length}
              subtitle="Defina quais são as métricas que serão analisadas no relatório arrastando o elemento desejado abaixo"
            />
          }
          collapsible
          bordered
          className="metricas-panel"
          defaultExpanded
        >
          <DndComponentMemo
            key="preco"
            dndName="preco"
            dndItems={typeof id === 'string' ? priceEditList.editItems : dndPriceItems}
            columnTitles={['Elementos disponíveis', 'Elementos selecionados (colunas)']}
            elementLeft={<ColumnSectionTitle title="Preços" />}
            elementRight={
              <div className="text-border-wrapper">
                <span className="text">Preços</span>
                <div className="toggle-container">
                  {PRICE_TYPE_TOGGLE_DATA.map(price => (
                    <div className="toggle-wrapper">
                      <Toggle
                        disabled={!consolidadoData.preco.length}
                        size="sm"
                        name={price.name}
                        checked={priceType[price.type]}
                        onChange={checked => handlePriceType(checked, price.type)}
                      />
                      <label htmlFor={price.name}>{price.label}</label>
                    </div>
                  ))}
                </div>
                <div className="side-border" />
              </div>
            }
          />
          <DndComponentMemo
            key="quantidade"
            dndName="quantidade"
            dndItems={typeof id === 'string' ? quantityEditList.editItems : dndQuantityItems}
            elementLeft={<ColumnSectionTitle title="Quantidade" />}
            elementRight={<ColumnSectionTitle title="Quantidade" />}
          />
          <DndComponentMemo
            key="comFiltro"
            dndName="comFiltro"
            dndItems={typeof id === 'string' ? withFiltersEditList.editItems : dndWithFiltersItems}
            elementLeft={<ColumnSectionTitle title="Outros" />}
            elementRight={<ColumnSectionTitle title="Outros" />}
          />
        </Panel>
      </Content>
      <ConsolidateItems editId={id} consolidateSelectedList={consolidateSelectedList} setConsolidateSelectedList={setConsolidateSelectedList} />
      <Content className="generate-report-wrapper">
        {typeof id === 'string' ? (
          <>
            <Button id="back-report-button" className="edit-go-back-button" appearance="ghost" onClick={onClickGoBack}>
              Voltar
            </Button>
            <Button id="save-edit-report" className="edit-save-report-button" appearance="primary" onClick={handleSaveEditedReport}>
              <img src={saveIcon} alt="" />
              Salvar relatório
            </Button>
          </>
        ) : (
          <>
            <Button id="save-report" className="save-report-button" appearance="ghost" onClick={handleSaveReport}>
              <img src={saveIcon} alt="" />
              Salvar relatório
            </Button>
            <Button id="generate-report" className="generate-report-button" appearance="primary" onClick={handleOpenModalGenerate}>
              Gerar relatório
            </Button>
          </>
        )}
      </Content>
      <ModalUnderstandBetter show={showModalUnderstandBetter} onCancel={() => setShowModalUnderstandBetter(false)} />
      <ModalConfirm
        show={showModalConfirmDownload}
        modalWidth="500px"
        titleFontSize="16px"
        textAlign="center"
        footerAlign="center"
        closeButton={false}
        icon={fileDownloadStatus.showModalConfirmDownload?.icon}
        title={fileDownloadStatus.showModalConfirmDownload?.title}
        message={fileDownloadStatus.showModalConfirmDownload?.message}
        confirmButton="salvar relatório"
        cancelButton={fileDownloadStatus.showModalConfirmDownload?.confirmButton}
        onConfirm={handleSaveReport}
        onCancel={handleSelectGenerateReportModal}
      />
      <ModalSaveReport show={showModalSaveReport} dataCounter={dataCounter} loadingCounter={isFetching} onCancel={() => setShowModalSaveReport(false)} />
    </>
  );
};
export default withRouter(CreateReportContent);
